import { apiClient } from '@/lib/api'
import type {
  StandardResponse,
  PaginatedStandardResponse,
  PaginationInfo,
  ErrorResponse,
} from '@/types/api-response'
import { isErrorResponse, extractData, extractPaginatedData } from '@/types/api-response'
import type {
  AuditLogDetail,
  AuditLogFacets,
  AuditLogListFilters,
  AuditLogListItem,
} from '@/types/admin'

/**
 * Turns the screen's filter state into the query string both the list and the export accept.
 *
 * The two endpoints take the same filter names on purpose, so an export always contains exactly
 * what the screen was showing. Paging is the only difference and is added by the caller.
 */
function buildFilterParams(filters: AuditLogListFilters): URLSearchParams {
  const params = new URLSearchParams()

  if (filters.search) params.append('search', filters.search)
  if (filters.action) params.append('action', filters.action)
  if (filters.entityType) params.append('entityType', filters.entityType)
  if (filters.entityId) params.append('entityId', filters.entityId)
  if (filters.userId) params.append('userId', filters.userId)
  if (filters.tenantId) params.append('tenantId', filters.tenantId)
  if (filters.fromDate) params.append('fromDate', filters.fromDate)
  if (filters.toDate) params.append('toDate', filters.toDate)

  return params
}

export const adminAuditService = {
  /**
   * Get one page of audit logs.
   * Returns: { data: AuditLogListItem[]; pagination: PaginationInfo }
   */
  async getAuditLogs(
    filters: AuditLogListFilters
  ): Promise<{ data: AuditLogListItem[]; pagination: PaginationInfo }> {
    const params = buildFilterParams(filters)

    params.append('page', filters.page.toString())

    // The API calls the page size "limit"; the screen calls it pageSize.
    params.append('limit', filters.pageSize.toString())

    const response = await apiClient.get<
      PaginatedStandardResponse<AuditLogListItem> | ErrorResponse
    >(`/api/v1/admin/audit-logs?${params.toString()}`)

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractPaginatedData(response.data)
  },

  /**
   * Get the values the filter dropdowns should offer, read from the rows that exist.
   * Returns: AuditLogFacets
   */
  async getAuditLogFilters(): Promise<AuditLogFacets> {
    const response = await apiClient.get<StandardResponse<AuditLogFacets> | ErrorResponse>(
      '/api/v1/admin/audit-logs/filters'
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractData(response.data)
  },

  /**
   * Get the full row behind a list entry — user agent, request ID and the JSON state columns the
   * list does not carry.
   * Returns: AuditLogDetail
   */
  async getAuditLogById(id: string): Promise<AuditLogDetail> {
    const response = await apiClient.get<StandardResponse<AuditLogDetail> | ErrorResponse>(
      `/api/v1/admin/audit-logs/${id}`
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractData(response.data)
  },

  /**
   * Export the filtered rows as CSV or JSON.
   * Returns: Blob (file download)
   */
  async exportAuditLogs(format: 'csv' | 'json', filters: AuditLogListFilters): Promise<Blob> {
    const params = buildFilterParams(filters)
    params.append('format', format)

    const response = await apiClient.get(`/api/v1/admin/audit-logs/export?${params.toString()}`, {
      responseType: 'blob',
    })

    return response.data
  },
}
